import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import Page from "./Page";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import useAdpConfiguration from "../../hooks/useAdpConfiguration";

const TRANSACTION_TYPES = ["call", "mileage", "expense", "adjustment"];
const EXPENSE_TYPES = [
  "mileage",
  "parking",
  "meal",
  "accommodation",
  "supplies",
  "other",
];

const emptyCodeForm = {
  id: null,
  codeType: "HOURS",
  code: "",
  description: "",
  value: "",
  isActive: true,
  sortOrder: 0,
};

const emptyMappingForm = {
  id: null,
  clientId: "",
  transactionType: "call",
  expenseType: "",
  adjustmentType: "",
  adpCodeId: "",
  isActive: true,
  notes: "",
};

const AdpConfiguration = () => {
  const {
    loading,
    adpCodes,
    clients,
    mappings,
    adjustmentTypes,
    loadAll,
    saveAdpCode,
    removeAdpCode,
    saveMapping,
    removeMapping,
  } = useAdpConfiguration();

  const [tabIndex, setTabIndex] = useState(0);
  const [openCodeDialog, setOpenCodeDialog] = useState(false);
  const [openMappingDialog, setOpenMappingDialog] = useState(false);
  const [codeForm, setCodeForm] = useState(emptyCodeForm);
  const [mappingForm, setMappingForm] = useState(emptyMappingForm);
  const [deleteTarget, setDeleteTarget] = useState({ type: null, id: null });

  useEffect(() => {
    loadAll();
  }, []);

  const sortedCodes = useMemo(() => {
    return [...(adpCodes || [])].sort((a, b) => {
      const typeCompare = String(a.codeType || "").localeCompare(
        String(b.codeType || ""),
      );
      if (typeCompare !== 0) return typeCompare;

      const sortCompare = (a.sortOrder || 0) - (b.sortOrder || 0);
      if (sortCompare !== 0) return sortCompare;

      return String(a.code || "").localeCompare(String(b.code || ""));
    });
  }, [adpCodes]);

  const activeCodes = useMemo(
    () => (adpCodes || []).filter((item) => item.isActive),
    [adpCodes],
  );

  const handleToolbarClick = (action) => {
    if (action === "refresh") {
      loadAll();
    }
  };

  const openAddCodeDialog = () => {
    setCodeForm(emptyCodeForm);
    setOpenCodeDialog(true);
  };

  const openEditCodeDialog = (code) => {
    setCodeForm({
      id: code.id,
      codeType: code.codeType || "HOURS",
      code: code.code || "",
      description: code.description || "",
      value: code.value ?? "",
      isActive: code.isActive !== false,
      sortOrder: code.sortOrder || 0,
    });
    setOpenCodeDialog(true);
  };

  const handleSaveCode = async () => {
    const success = await saveAdpCode(codeForm);
    if (success) {
      setOpenCodeDialog(false);
      setCodeForm(emptyCodeForm);
    }
  };

  const openAddMappingDialog = () => {
    setMappingForm(emptyMappingForm);
    setOpenMappingDialog(true);
  };

  const openEditMappingDialog = (mapping) => {
    setMappingForm({
      id: mapping.id,
      clientId: mapping.clientId || "",
      transactionType: mapping.transactionType || "call",
      expenseType: mapping.expenseType || "",
      adjustmentType: mapping.adjustmentType || "",
      adpCodeId: mapping.adpCodeId || "",
      isActive: mapping.isActive !== false,
      notes: mapping.notes || "",
    });
    setOpenMappingDialog(true);
  };

  const handleSaveMapping = async () => {
    const success = await saveMapping(mappingForm);
    if (success) {
      setOpenMappingDialog(false);
      setMappingForm(emptyMappingForm);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id || !deleteTarget?.type) return;

    if (deleteTarget.type === "code") {
      await removeAdpCode(deleteTarget.id);
    }

    if (deleteTarget.type === "mapping") {
      await removeMapping(deleteTarget.id);
    }

    setDeleteTarget({ type: null, id: null });
  };

  return (
    <Page
      pageName="adpConfiguration"
      includeToolbar={true}
      onToolItemClick={handleToolbarClick}
    >
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
      >
        <Tabs
          value={tabIndex}
          onChange={(event, value) => setTabIndex(value)}
        >
          <Tab label="ADP Codes" />
          <Tab label="Client Mappings" />
        </Tabs>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            py={5}
          >
            <CircularProgress />
          </Box>
        ) : tabIndex === 0 ? (
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">ADP Master Codes</Typography>
                <Button
                  variant="contained"
                  onClick={openAddCodeDialog}
                >
                  Add Code
                </Button>
              </Box>

              {sortedCodes.length === 0 ? (
                <Typography color="text.secondary">
                  No ADP codes found
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Value</TableCell>
                      <TableCell align="right">Sort</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCodes.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.codeType}</TableCell>
                        <TableCell>{item.code}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">{item.value ?? "-"}</TableCell>
                        <TableCell align="right">
                          {item.sortOrder || 0}
                        </TableCell>
                        <TableCell>
                          {item.isActive ? "Active" : "Inactive"}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            onClick={() => openEditCodeDialog(item)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() =>
                              setDeleteTarget({ type: "code", id: item.id })
                            }
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">Client ADP Code Mappings</Typography>
                <Button
                  variant="contained"
                  onClick={openAddMappingDialog}
                >
                  Add Mapping
                </Button>
              </Box>

              {mappings.length === 0 ? (
                <Typography color="text.secondary">
                  No ADP mappings found
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell>Transaction</TableCell>
                      <TableCell>Subtype</TableCell>
                      <TableCell>ADP Code</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappings.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.client?.name || "-"}</TableCell>
                        <TableCell>{item.transactionType}</TableCell>
                        <TableCell>
                          {item.expenseType || item.adjustmentType || "-"}
                        </TableCell>
                        <TableCell>
                          {item.adpCode?.code
                            ? `${item.adpCode.codeType} - ${item.adpCode.code}`
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {item.isActive ? "Active" : "Inactive"}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            onClick={() => openEditMappingDialog(item)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() =>
                              setDeleteTarget({ type: "mapping", id: item.id })
                            }
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      <Dialog
        open={openCodeDialog}
        onClose={() => setOpenCodeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {codeForm.id ? "Edit ADP Code" : "Add ADP Code"}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}
        >
          <FormControl fullWidth>
            <InputLabel id="adp-code-type-label">Code Type</InputLabel>
            <Select
              labelId="adp-code-type-label"
              value={codeForm.codeType}
              label="Code Type"
              onChange={(event) =>
                setCodeForm((prev) => ({
                  ...prev,
                  codeType: event.target.value,
                }))
              }
            >
              <MenuItem value="HOURS">HOURS</MenuItem>
              <MenuItem value="EARNINGS">EARNINGS</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Code"
            value={codeForm.code}
            onChange={(event) =>
              setCodeForm((prev) => ({ ...prev, code: event.target.value }))
            }
            fullWidth
            required
          />

          <TextField
            label="Description"
            value={codeForm.description}
            onChange={(event) =>
              setCodeForm((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
            fullWidth
            required
          />

          <TextField
            label="Value (optional)"
            type="number"
            value={codeForm.value}
            onChange={(event) =>
              setCodeForm((prev) => ({ ...prev, value: event.target.value }))
            }
            fullWidth
          />

          <TextField
            label="Sort Order"
            type="number"
            value={codeForm.sortOrder}
            onChange={(event) =>
              setCodeForm((prev) => ({
                ...prev,
                sortOrder: Number(event.target.value || 0),
              }))
            }
            fullWidth
          />

          <FormControlLabel
            control={
              <Switch
                checked={codeForm.isActive}
                onChange={(event) =>
                  setCodeForm((prev) => ({
                    ...prev,
                    isActive: event.target.checked,
                  }))
                }
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCodeDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCode}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openMappingDialog}
        onClose={() => setOpenMappingDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {mappingForm.id ? "Edit Mapping" : "Add Mapping"}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}
        >
          <FormControl fullWidth>
            <InputLabel id="adp-mapping-client-label">Client</InputLabel>
            <Select
              labelId="adp-mapping-client-label"
              value={mappingForm.clientId}
              label="Client"
              onChange={(event) =>
                setMappingForm((prev) => ({
                  ...prev,
                  clientId: event.target.value,
                }))
              }
            >
              {(clients || []).map((client) => (
                <MenuItem
                  key={client.id}
                  value={client.id}
                >
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="adp-mapping-type-label">
              Transaction Type
            </InputLabel>
            <Select
              labelId="adp-mapping-type-label"
              value={mappingForm.transactionType}
              label="Transaction Type"
              onChange={(event) =>
                setMappingForm((prev) => ({
                  ...prev,
                  transactionType: event.target.value,
                  expenseType: "",
                  adjustmentType: "",
                }))
              }
            >
              {TRANSACTION_TYPES.map((type) => (
                <MenuItem
                  key={type}
                  value={type}
                >
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {mappingForm.transactionType === "expense" && (
            <FormControl fullWidth>
              <InputLabel id="adp-mapping-expense-type-label">
                Expense Type
              </InputLabel>
              <Select
                labelId="adp-mapping-expense-type-label"
                value={mappingForm.expenseType}
                label="Expense Type"
                onChange={(event) =>
                  setMappingForm((prev) => ({
                    ...prev,
                    expenseType: event.target.value,
                  }))
                }
              >
                {EXPENSE_TYPES.map((type) => (
                  <MenuItem
                    key={type}
                    value={type}
                  >
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {mappingForm.transactionType === "adjustment" && (
            <FormControl fullWidth>
              <InputLabel id="adp-mapping-adjustment-type-label">
                Adjustment Type
              </InputLabel>
              <Select
                labelId="adp-mapping-adjustment-type-label"
                value={mappingForm.adjustmentType}
                label="Adjustment Type"
                onChange={(event) =>
                  setMappingForm((prev) => ({
                    ...prev,
                    adjustmentType: event.target.value,
                  }))
                }
              >
                {(adjustmentTypes || [])
                  .filter((type) => type?.isActive !== false)
                  .map((type) => (
                    <MenuItem
                      key={type.id || type.key}
                      value={type.key}
                    >
                      {type.label || type.key}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth>
            <InputLabel id="adp-mapping-code-label">ADP Code</InputLabel>
            <Select
              labelId="adp-mapping-code-label"
              value={mappingForm.adpCodeId}
              label="ADP Code"
              onChange={(event) =>
                setMappingForm((prev) => ({
                  ...prev,
                  adpCodeId: event.target.value,
                }))
              }
            >
              {activeCodes.map((code) => (
                <MenuItem
                  key={code.id}
                  value={code.id}
                >
                  {`${code.codeType} - ${code.code}: ${code.description}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Notes"
            value={mappingForm.notes}
            onChange={(event) =>
              setMappingForm((prev) => ({ ...prev, notes: event.target.value }))
            }
            fullWidth
            multiline
            rows={2}
          />

          <FormControlLabel
            control={
              <Switch
                checked={mappingForm.isActive}
                onChange={(event) =>
                  setMappingForm((prev) => ({
                    ...prev,
                    isActive: event.target.checked,
                  }))
                }
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMappingDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveMapping}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog_YesNo
        open={Boolean(deleteTarget.id)}
        onClose={() => setDeleteTarget({ type: null, id: null })}
        title="Confirm Delete"
        message="This action cannot be undone. Do you want to continue?"
        onYes={confirmDelete}
        onNo={() => setDeleteTarget({ type: null, id: null })}
      />
    </Page>
  );
};

export default AdpConfiguration;
